import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { toast } from 'sonner';
import { getCachedInitial, checkAndSplitString, fetcher } from '@lib/utils';
import { addNewTemplatesWithNameTag } from "@/lib/db/template";
import { auth } from '@/app/(auth)/auth';
import { UUID } from 'crypto';
import { ExtractedInitial } from '@/lib/type';
import useGlobalStore from './global-store';
import { sslog } from '@/lib/utils/sslog';
import { JSONContent } from 'novel';
// import { Template } from '@/lib/db/schema';

export interface Template {
  id: string;
  title: string;
  name: string;
  description: string;
  role: string;
  content: any; // JSON data
  createdAt: Date;
  sourceId: string | null;
  industry: string | null;
  type: string | null;
  tag: string | null;
  fee: string;
  userId: string | null;
  initialData?: ExtractedInitial;
  disableMentor?: boolean;
}

interface Suggest {
  title: string;
  label: string;
  action: string;
}

// interface InitialData {
//   placeHolder?: string;
//   suggestions?: string[];
//   steps?: any[];
//   disable?: boolean;
//   disableMentor?: boolean | false;
//   jsonBlocks?: string[];
// }

const emptyTemplate: Template = {
  id: '', // 默认为空字符串
  title: '',
  name: '',
  description: '',
  role: '',
  content: {}, // 默认为空对象
  createdAt: new Date(), // 当前时间
  sourceId: null,
  industry: null,
  type: null,
  tag: null,
  fee: '0', // 默认费用为 "0"
  userId: null,
  initialData: undefined, // 可选字段，默认为 undefined
  disableMentor: false, // 可选字段，默认为 false
};

interface TemplateStore {
  initialTemplate: Template | null;
  templates: Template[];
  selectedTemplates: Template[] | null;
  editingTemplate: Template | null;
  loading: boolean;
  error: string | null;
  flowTemplateName?: string;
  templateEditing: boolean;
  editingLength: number;
  setTemplateEditing: (templateEditing: boolean) => void;
  setEditingLength: (editingLength: number) => void;

  setFlowTemplateName: (flowTemplateName: string) => void;
  setInitialTemplate: ( initialTemplate:Template) => void;
  setEditingTemplate: ( editingTemplate:Template) => void;
  addTemplateByName: (name: string, title:string, description:string) => void;
  fetchTemplates: () => void;
  fetchTemplateById: (id: string) => void;
  fetchTemplatesByName: (name: string, fresh?:boolean) => void;
  fetchTemplateByRoleName: (role: string, name: string) => void;
  updateTemplate: (id: string, updates: Partial<Template>) => void;
  updateTemplateContent: (id: string, name: string, role: string, content: any) => void;
}

// export const createMyStore = () => {
//   return create<TemplateStore>((set) => ({

const useTemplateStore = create<TemplateStore>()( // 所有使用的组件都共享一个store
// export const createTemplateStore = () => create<TemplateStore>()(  // 每个组件都自己动态创建一个store

  devtools((set, get) => ({
    initialTemplate: null,
    templates: [],
    selectedTemplates: null,
    editingTemplate: null,
    loading: false, // Set to false initially
    error: null,
    templateEditing: false,
    editingLength: 0,
    setTemplateEditing: (templateEditing: boolean) => { set({templateEditing})},
    setEditingLength: (editingLength: number) => { set({editingLength})},
    setFlowTemplateName: (flowTemplateName: string) => { set({flowTemplateName})},
    setEditingTemplate: ( editingTemplate:Template) => set({ editingTemplate }),
    addTemplateByName: async (name: string, title:string, description:string) => {
      const result = checkAndSplitString(name);       
      sslog(`limk.log addTemplateByName name:`, {name, result});

      if (!result || !Array.isArray(result) || result.length < 3 ) return;

      // const input = "novel#world@background";  // 输出: ["novel", "world", "background"]
      if (!result || !Array.isArray(result) || result.length < 3) {
        toast.error('输入的字符串不符合要求, 请输入类似格式：novel#world@background');
        return;
      }

      const updatePromise = fetch(`/api/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title,
          name: name,
          industry: result[0], 
          type: result[1],
          tag: result[2],
          description: description,
          content: {},
        }),
      });

      toast.promise(updatePromise, {
        loading: 'Updating template...',
        success: () => {
          set((state) => ({}));
          return 'Template updated successfully';
        },
        error: 'Failed to update template',
      });

      try {
        const response = await updatePromise;
        const newTemplates = await response.json();
        console.log(`limk.log addTemplateByName newTemplates:${JSON.stringify(newTemplates)}`);

        // set({ selectedTemplates: newTemplates, loading: false });
        // set((state) => ({}));
      } catch (error) {
        set({ error: 'Failed to update template' });
      }
    },
    fetchTemplates: async () => {
      set({ loading: true, error: null });
      try {
        const response = await fetch('/api/template');
        const data: Template[] = await response.json();
        // console.log(`limk.log fetchTemplates data:${JSON.stringify(data.filter(t=>t.industry))}`);
        set({ templates: data, loading: false });
      } catch (error) {
        set({ error: 'Failed to fetch templates', loading: false });
      }
    },
    fetchTemplateById: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const response = await fetch(`/api/template/${id}`);
        const data = await response.json();
        console.log(`limk.log fetchTemplateById data:${JSON.stringify(data)}`);
        set({ editingTemplate: data, loading: false });
      } catch (error) {
        set({ error: 'Failed to fetch template', loading: false });
      }
    },
    setInitialTemplate: (template: Template) => {
      const { user }  = useGlobalStore.getState();
      sslog(`limk.log setInitialTemplate initial user template:`, {name: template.name});

      if (template.initialData) {
        set({ initialTemplate:template });
      } else {
        const initial: Template = template;
        initial.initialData = getCachedInitial(`${template.name}.initial.${user.email}`, initial.content);
        // initial.initialData = { placeHolder: ini.placeHolder, suggestions: ini.suggestions, disableMentor: ini.disableMentor, jsonBlocks:ini.jsonBlocks };
        // console.log(`limk.log fetchTemplatesByName initial:${JSON.stringify(initial.initialData)}`);
        initial.disableMentor = initial.initialData.disableMentor;
        set({ initialTemplate: initial });
      }
    },
    fetchTemplatesByName: async (name: string, fresh?:boolean) => {
      set({ loading: true, error: null });
      if (!name && name.length == 0) return;
      try {
        const { flowTemplateName } = get();
        const data = await fetcher(`/api/template?name=${name}&&flowTemplateName=${flowTemplateName}`, fresh);
        sslog(`limk.log api.user.llogtemplateStore.fetchTemplatesByName ${name} response:`, data.length);

        // 使用函数式更新确保获取最新状态
        // @ts-ignore
        set((state) => {
          // const current = fresh ? [] : state.selectedTemplates ?? [];
          const current = state.selectedTemplates ?? [];

          // const unique = [
          //   ...current,
          //   ...data.filter(newT => !current.some(existing => existing.id === newT.id))
          // ];

          // 创建临时对象存储键值映射（假设唯一键为name+role）
          // 添加类型声明解决索引签名问题
          const tempMap = current.reduce<Record<string, Template>>((acc, item) => {
            acc[`${item.name}-${item.role}`] = item;
            return acc;
          }, {});

          // 使用已定义类型的映射继续操作
          const updatedMap = data.reduce((acc: { [x: string]: any; }, item: { name: any; role: any; }) => {
            acc[`${item.name}-${item.role}`] = item;
            return acc;
          }, tempMap);

          /// sslog(`limk.log templateStore.fetchTemplatesByName ${name} updatedMap:`, updatedMap);

          const unique = Object.values(updatedMap) as Template[];
    
          const preName = name.split('@')[0]; // 流程模版也算
          // 同步处理initial模板
          const initial = unique.find((t: { name: string; role: string; }) => t.name.startsWith(preName) && t.role === 'initial');
          
          /// sslog(`limk.log templateStore.fetchTemplatesByName ${name} initial:`, initial);
          // unique.forEach((t => sslog(`limk.log templateStore.fetchTemplatesByName ${name} unique:`, t)))

          /// sslog(`limk.log fetchTemplatesByName ${name} unique:`, unique);

          if (initial) {
            const { user }  = useGlobalStore.getState();
            initial.initialData = getCachedInitial(`${name}.initial.${user.email}`, initial.content);
            initial.disableMentor = initial.initialData.disableMentor;
            // console.log(`limk.log fetchTemplatesByName ${name} initial:${JSON.stringify(initial)}`);
            return { 
              selectedTemplates: unique,
              initialTemplate: initial // 直接更新避免二次get()
            };
          }
    
          return { selectedTemplates: unique, initialTemplate: {} };
        });
    
      } catch (error) {
        console.error('Fetch failed:', error);
      }
      
      /*
      try {
        // const response = await fetch(`/api/template?name=novel#world@background`);//${name}`);
        const   data = await fetcher(`/api/template?name=${name}`, fresh);
        // const response = await fetch(`/api/template?name=${name}`);
        // const data = await response.json();
        console.log(`limk.log fetchTemplatesByName ${name} data:${JSON.stringify(data)}`);
        // set({ selectedTemplates: data, loading: false });
        const inits1 = data.filter((t: Template) => (t.name === name && t.role == 'initial'));

        console.log(`limk.log fetchTemplatesByName ${name} data:${JSON.stringify(inits1)}`);
        const initial1: Template = inits1[0];
        console.log(`limk.log fetchTemplatesByName: set initialTemplate:`, initial1);

        // limk.log 部分更新 selectedTemplates 的逻辑
        if (get().selectedTemplates === null) {
          // 如果是 fresh 模式，直接覆盖 selectedTemplates
          set({ selectedTemplates: data });
        } else {
          // 否则，将新数据追加到现有数组中，并去重
          const currentTemplates = get().selectedTemplates ?? [];
          const uniqueTemplates = [
            ...currentTemplates,
            ...data.filter(
              (newTemplate:Template) => !currentTemplates.some((existing) => existing.id === newTemplate.id)
            ),
          ];
          console.log(`limk.log fetchTemplatesByName: set selectedTemplates:`, uniqueTemplates);
          set({ selectedTemplates: uniqueTemplates });
        }
        const inits = data.filter((t: Template) => (t.name === name && t.role == 'initial'));
        if (inits.length > 0) {
          const initial: Template = inits[0];
          console.log(`limk.log fetchTemplatesByName: set initialTemplate:`, initial);
          get().setInitialTemplate(initial);
        } else {
          console.log(`limk.log fetchTemplatesByName: set initialTemplate: null`);
          set({ initialTemplate: emptyTemplate });
        }
      } catch (error) {
        set({ error: 'Failed to fetch template', loading: false });
      }
        */
    },
    fetchTemplateByRoleName: async (role: string, name: string) => {
      set({ loading: true, error: null });
      try {
        const { selectedTemplates } = get();
        if (selectedTemplates && selectedTemplates.length > 0) {
          console.log(`limk.log editingTemplate from state selectedTemplates:${JSON.stringify(selectedTemplates)}`);
          set({ editingTemplate: selectedTemplates.filter(t => (t.name ==name && t.role == role))[0], loading: false });
          return;
        }

        const response = await fetch(`/api/template?role=${role}&&name=${name}`);
        const data = await response.json();
        console.log(`limk.log fetchTemplateByRoleName data:${JSON.stringify(data)}`);
        if (data && data.length > 0) set({ editingTemplate: data[0], loading: false });
      } catch (error) {
        set({ error: 'Failed to fetch template', loading: false });
      }
    },
    updateTemplate: async (id: string, updates: Partial<Template>) => {
      // Your implementation here
    },
    updateTemplateContent: async (id: string, name: string, role: string, content: any) => {
      const updatePromise = fetch(`/api/template/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, role, content }),
      });

      toast.promise(updatePromise, {
        loading: 'Updating template content...',
        success: () => {
          set((state) => ({
            editingTemplate: {
              ...state.editingTemplate!,
              content,
            },
          }));
          return 'Template content updated successfully';
        },
        error: 'Failed to update template content',
      });

      try {
        const response = await updatePromise;
        const data = await response.json();
        console.log('limk.log.templateStore updateTemplateContent response:', data);
        set((state) => ({
          editingTemplate: {
            ...state.editingTemplate!,
            content: content,
          },
        }));
      } catch (error) {
        set({ error: 'Failed to update template content' });
      }
    },
  }))
);


export default useTemplateStore;




/*























// store/templateStore.ts
import create  from 'zustand';
import { devtools } from 'zustand/middleware';
import { toast } from 'sonner';
import { string } from 'zod';
import { getCachedInitial } from '@lib/utils'

interface Template {
  id: string;
  title: string;
  name: string;
  description: string;
  role: string;
  content: any; // JSON data
  createdAt: string;
  initialData?: InitialData;
}

interface Suggest {
    title: string;
    label: string;
    action: string;
}

interface InitialData {
    placeHolder?: string;
    suggestions?: string[];
    steps?: any[];
}

interface TemplateStore {
  initialTemplate: Template | null;
  templates: Template[];                 // templates 没有附带content信息，只有模版的基本信息
  selectedTemplates: Template[] | null;  
  editingTemplate: Template | null;     // 任务页面点击的template卡片的右边上下两个配置按钮的点选，分别代表当前设置的是mentor还是doer角色的配置
  loading: boolean;
  error: string | null;
  addTemplateByName: (name: string) => void;
  fetchTemplates: () => void;
  fetchTemplateById: (id: string) => void;
  fetchTemplatesByName: (name: string) => void;
  fetchTemplateByRoleName: (role: string, name: string) => void;
  updateTemplate: (id: string, updates: Partial<Template>) => void;
  updateTemplateContent: (id: string, name: string, role:string, content: any) => void;
}

const useTemplateStore = create<TemplateStore>()(
  devtools((set:any) => ({
    initialTemplate: null,
    templates: [],
    selectedTemplates: [],
    editingTemplate: null,
    loading: true,
    error: null,
    addTemplateByName: async (name: string) => {

      console.log(`limk.log addTemplateByName name:${name}`);

      const updatePromise = fetch(`/api/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: "点击左上角编辑",
          name: name,
          role: "initial",
          description: "点击左上角编辑描述...",
          content: {},
        }),
      });

      toast.promise(updatePromise, {
        loading: 'Updating template...',
        success: () => {
          set((state) => ({
            // templates: state.templates.map((template) =>
            //   template.id === id ? { ...template, ...updates } : template
            // ),
          }));
          return 'Template updated successfully';
        },
        error: 'Failed to update template',
      });

      try {
        const response = await updatePromise;
        const data = await response.json();
        set((state) => ({
          // templates: state.templates.map((template) =>
          //   template.id === id ? { ...template, ...data } : template
          // ),
        }));
      } catch (error) {
        set({ error: 'Failed to update template' });
      }
    },
    fetchTemplates: async () => {
      set({ loading: true, error: null });
      try {
        const response = await fetch('/api/template');
        const data:Template[] = await response.json();
        console.log(`limk.log fetchTemplates data:${JSON.stringify(data)}`);
        // const temps = data.concat([{
        //     "id": "00000000-0000-0000-0000-000000000000",
        //     "title": " + 添加模板",
        //     "name": "New",
        //     "role": "initial",
        //     "description": "设计一个新的任务范式...",
        //     "content": {},
        //     "createdAt": "2024-11-20T17:13:37.241Z"
        // }])
        set({ templates: data, loading: false });
      } catch (error) {
        set({ error: 'Failed to fetch templates', loading: false });
      }
    },
    fetchTemplateById: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const response = await fetch(`/api/template/${id}`);
        const data = await response.json();
        console.log(`limk.log fetchTemplateById data:${JSON.stringify(data)}`);
        set({ editingTemplate: data, loading: false });
      } catch (error) {
        set({ error: 'Failed to fetch template', loading: false });
      }
    },
    fetchTemplatesByName: async (name: string) => {
        set({ loading: true, error: null });
        try {
          const response = await fetch(`/api/template?name=${name}`);
          const data = await response.json();
          console.log(`limk.log fetchTemplatesByName data:${JSON.stringify(data)}`);
          set({ selectedTemplates: data, loading: false });
          const inits = data.filter((t:Template)=>t.role=='initial');
          if (inits.length > 0) {
            const initial:Template = inits[0];

            const ini =  getCachedInitial(`${name}.initial`, initial.content);
            initial.initialData = {placeHolder: ini.placeHolder, suggestions: ini.suggestions};
            console.log(`limk.log fetchTemplatesByName initial:${JSON.stringify(initial)}`);

            set({initialTemplate: initial});
          }
        } catch (error) {
          set({ error: 'Failed to fetch template', loading: false });
        }
    },
    fetchTemplateByRoleName: async (role: string, name:string) => {
        set({ loading: true, error: null });
        try {
         const { selectedTemplates } = useTemplateStore.getState();
         if (selectedTemplates && selectedTemplates.length > 0) {
            console.log(`limk.log editingTemplate from state selectedTemplates:${JSON.stringify(selectedTemplates)}`);
            set({ editingTemplate: selectedTemplates.filter(t=>t.role==role)[0], loading: false });
            return;
         }

          const response = await fetch(`/api/template?role=${role}&&name=${name}`);
          const data = await response.json();
          console.log(`limk.log fetchTemplateByRoleName data:${JSON.stringify(data)}`);
          if (data && data.length > 0) set({ editingTemplate: data[0], loading: false });
        } catch (error) {
          set({ error: 'Failed to fetch template', loading: false });
        }
    },
    updateTemplate: async (id: string, updates: Partial<Template>) => {
      // const updatePromise = fetch(`/api/template/${id}`, {
      //   method: 'PATCH',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(updates),
      // });

      // toast.promise(updatePromise, {
      //   loading: 'Updating template...',
      //   success: () => {
      //     set((state) => ({
      //       templates: state.templates.map((template) =>
      //         template.id === id ? { ...template, ...updates } : template
      //       ),
      //     }));
      //     return 'Template updated successfully';
      //   },
      //   error: 'Failed to update template',
      // });

      // try {
      //   const response = await updatePromise;
      //   const data = await response.json();
      //   set((state) => ({
      //     templates: state.templates.map((template) =>
      //       template.id === id ? { ...template, ...data } : template
      //     ),
      //   }));
      // } catch (error) {
      //   set({ error: 'Failed to update template' });
      // }
    },
    updateTemplateContent: async (id:string, name: string, role:string, content: any) => {
      const updatePromise = fetch(`/api/template/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, role, content }),
      });

      toast.promise(updatePromise, {
        loading: 'Updating template content...',
        success: () => {
          set((state) => ({
            editingTemplate: {
              ...state.editingTemplate!,
              content,
            },
          }));
          return 'Template content updated successfully';
        },
        error: 'Failed to update template content',
      });

      try {
        const response = await updatePromise;
        const data = await response.json();
        set((state) => ({
          editingTemplate: {
            ...state.editingTemplate!,
            content: content,
          },
        }));
      } catch (error) {
        set({ error: 'Failed to update template content' });
      }
    },
  }))
);

export default useTemplateStore;

*/